import React, { useState, useEffect } from 'react';
import { Card, Typography, Alert, Form, DatePicker, Space, Divider, Tag, Button } from 'antd';
import { ClockCircleOutlined, SafetyCertificateOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { GET_ACCOUNT } from '../../../mutations/account';
import locale from 'antd/es/date-picker/locale/pt_BR';
import dayjs from 'dayjs';
import { isCurrentTimeValid, getNextValidCampaignTime, isValidCampaignTime } from '../../../utils/campaignHoursUtils';

const { Title, Text, Paragraph } = Typography;

const SendStep = ({ totalMessagesCount, form: parentForm }) => {
  const { data: accountData } = useQuery(GET_ACCOUNT, {
    fetchPolicy: 'network-only'
  });
  const account = accountData?.account;
  const [localForm] = Form.useForm();
  const [nextValidTime, setNextValidTime] = useState(null);
  const [isCurrentValid, setIsCurrentValid] = useState(true);
  
  // Usar o form do componente pai se fornecido, senão usar o local
  const form = parentForm || localForm;
  
  // Usar configurações da conta ou valores padrão
  const throttlingEnabled = account?.campaignThrottlingEnabled ?? true;
  const minInterval = account?.campaignMinIntervalSeconds ?? 45;
  const maxInterval = account?.campaignMaxIntervalSeconds ?? 75;
  
  // Calcular tempo estimado para todas as mensagens
  const averageInterval = (minInterval + maxInterval) / 2;
  const totalTimeMinutes = Math.ceil((totalMessagesCount - 1) * averageInterval / 60);
  const hours = Math.floor(totalTimeMinutes / 60);
  const minutes = totalTimeMinutes % 60;
  
  const timeEstimate = hours > 0 
    ? `${hours}h ${minutes}min` 
    : `${minutes} minutos`;

  // Verificar horários válidos quando a conta é carregada
  useEffect(() => {
    if (account?.campaignHours) {
      const currentValid = isCurrentTimeValid(account.campaignHours);
      setIsCurrentValid(currentValid);
      
      if (!currentValid) {
        const nextValid = getNextValidCampaignTime(account.campaignHours);
        setNextValidTime(nextValid);
        
        // Se não é um horário válido, definir automaticamente o próximo horário válido
        if (nextValid) {
          form.setFieldsValue({
            scheduledFor: nextValid
          });
        }
      }
    }
  }, [account, form]);

  // Função personalizada para desabilitar horários não permitidos
  const disabledTime = (current) => {
    if (!current) return {};

    const disabledPastTime = {};
    const now = dayjs();

    if (current.isSame(now, 'day')) {
      disabledPastTime.disabledHours = () => Array.from({ length: now.hour() }, (_, i) => i);
      disabledPastTime.disabledMinutes = (selectedHour) => (
        selectedHour === now.hour() ? Array.from({ length: now.minute() }, (_, i) => i) : []
      );
    }

    if (!account?.campaignHours) return disabledPastTime;
    
    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][current.day()];
    const dayConfig = account.campaignHours[dayOfWeek];
    
    if (!dayConfig) return { disabledHours: () => Array.from({ length: 24 }, (_, i) => i) };
    
    if (dayConfig.always_open) return disabledPastTime;
    
    if (!dayConfig.intervals || dayConfig.intervals.length === 0) {
      return { disabledHours: () => Array.from({ length: 24 }, (_, i) => i) };
    }
    
    const disabledHours = [];
    
    for (let hour = 0; hour < 24; hour++) {
      let hourIsValid = false;
      
      for (const interval of dayConfig.intervals) {
        const startHour = parseInt(interval.start.split(':')[0]);
        const startMinute = parseInt(interval.start.split(':')[1]);
        const endHour = parseInt(interval.end.split(':')[0]);
        const endMinute = parseInt(interval.end.split(':')[1]);
        
        // Verificar se a hora está dentro do intervalo
        if (hour > startHour && hour < endHour) {
          hourIsValid = true;
          break;
        } else if (hour === startHour && hour === endHour) {
          // Mesmo horário de início e fim - verificar minutos
          hourIsValid = true;
          break;
        } else if (hour === startHour || hour === endHour) {
          // Hora de início ou fim - sempre válida
          hourIsValid = true;
          break;
        }
      }
      
      if (!hourIsValid) {
        disabledHours.push(hour);
      }
    }
    
    // Função para desabilitar minutos baseado nos intervalos
    const disabledMinutes = (selectedHour) => {
      // Inicialmente todos os minutos estão desabilitados
      const enabledMinutes = new Set();
      
      for (const interval of dayConfig.intervals) {
        const startHour = parseInt(interval.start.split(':')[0]);
        const startMinute = parseInt(interval.start.split(':')[1]);
        const endHour = parseInt(interval.end.split(':')[0]);
        const endMinute = parseInt(interval.end.split(':')[1]);
        
        // Se a hora selecionada está dentro do intervalo
        if (selectedHour > startHour && selectedHour < endHour) {
          // Todos os minutos são válidos
          for (let minute = 0; minute < 60; minute++) {
            enabledMinutes.add(minute);
          }
        } else if (selectedHour === startHour && selectedHour === endHour) {
          // Mesmo horário de início e fim - só os minutos do intervalo
          for (let minute = startMinute; minute <= endMinute; minute++) {
            enabledMinutes.add(minute);
          }
        } else if (selectedHour === startHour) {
          // Hora de início - minutos a partir do início
          for (let minute = startMinute; minute < 60; minute++) {
            enabledMinutes.add(minute);
          }
        } else if (selectedHour === endHour) {
          // Hora de fim - minutos até o fim
          for (let minute = 0; minute <= endMinute; minute++) {
            enabledMinutes.add(minute);
          }
        }
      }
      
      // Retornar minutos desabilitados (inverso dos habilitados)
      const disabled = [];
      for (let minute = 0; minute < 60; minute++) {
        if (!enabledMinutes.has(minute)) {
          disabled.push(minute);
        }
      }
      
      return disabled;
    };
    
    return {
      disabledHours: () => [...new Set([...(disabledPastTime.disabledHours?.() || []), ...disabledHours])],
      disabledMinutes: (selectedHour) => [...new Set([...(disabledPastTime.disabledMinutes?.(selectedHour) || []), ...disabledMinutes(selectedHour)])]
    };
  };

  // Desabilitar datas/horários que não estão nos horários permitidos
  const disabledDate = (current) => {
    if (!current) return false;
    
    if (current.isBefore(dayjs(), 'day')) return true;

    if (!account?.campaignHours) return false;
    
    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][current.day()];
    const dayConfig = account.campaignHours[dayOfWeek];
    
    // Se não tem configuração para este dia ou não tem intervalos, desabilitar
    if (!dayConfig || (!dayConfig.always_open && (!dayConfig.intervals || dayConfig.intervals.length === 0))) {
      return true;
    }
    
    return false;
  };

  return (
    <Card>
      <Title level={4}>
        <Space>
          <ClockCircleOutlined />
          Enviar Campanha
        </Space>
      </Title>
      
      {throttlingEnabled ? (
        <Alert
          message="Sistema Anti-SPAM Ativo"
          description={
            <div>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <SafetyCertificateOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                  <strong>Proteção contra bloqueio do WhatsApp:</strong>
                </div>
                <div>• As mensagens serão enviadas com intervalos aleatórios entre {minInterval} segundos e {Math.floor(maxInterval/60)}:{String(maxInterval%60).padStart(2, '0')} minuto</div>
                <div>• Isso evita que o WhatsApp identifique como SPAM e bloqueie seu número</div>
                <div>• <strong>{totalMessagesCount} mensagens</strong> serão enviadas em aproximadamente <strong>{timeEstimate}</strong></div>
              </Space>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      ) : (
        <Alert
          message="⚠️ Sistema Anti-SPAM Desativado"
          description={
            <div>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                  <strong>Atenção:</strong> O throttling está desabilitado nas configurações da conta
                </div>
                <div>• Todas as {totalMessagesCount} mensagens serão enviadas ao mesmo tempo</div>
                <div>• <strong>RISCO:</strong> Isso pode fazer com que o WhatsApp bloqueie seu número por SPAM</div>
                <div>• <Text type="secondary">Você pode ativar a proteção em Conta → Configurações Anti-SPAM WhatsApp</Text></div>
              </Space>
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}
      
      <Divider />
      
      {!isCurrentValid && nextValidTime && (
        <Alert
          message="Horário atual não disponível"
          description={
            <div>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <InfoCircleOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                  <strong>O horário atual não está dentro dos horários permitidos para campanhas.</strong>
                </div>
                <div>• Próximo horário disponível: <strong>{nextValidTime.format('DD/MM/YYYY às HH:mm')}</strong></div>
                <div>• O agendamento foi automaticamente ajustado para este horário</div>
                <div>• Você pode modificar manualmente se desejar outro horário válido</div>
              </Space>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}
      
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          name="scheduledFor"
          label="Agendar Envio"
          rules={[]}
        >
          <DatePicker 
            showTime 
            format="DD/MM/YYYY HH:mm" 
            placeholder="Selecione data e hora para agendar o envio"
            style={{ width: '300px' }}
            locale={locale}
            disabledDate={disabledDate}
            disabledTime={disabledTime}
            showNow={isCurrentValid}
            renderExtraFooter={() => (
              !isCurrentValid ? (
                <div style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0', color: '#999' }}>
                  <InfoCircleOutlined style={{ marginRight: 4 }} />
                  O botão "Agora" está desabilitado pois o horário atual não é permitido
                </div>
              ) : null
            )}
          />
        </Form.Item>
      </Form>
      
              <div style={{ marginTop: 16 }}>
        <Paragraph>
          <Text>
            <strong>Como funciona o agendamento:</strong>
          </Text>
        </Paragraph>
        <ul style={{ paddingLeft: 20 }}>
          <li>
            <Text>
              <strong>Envio imediato:</strong> Deixe o campo de data vazio para começar o envio {isCurrentValid ? 'agora' : 'no próximo horário válido'}
            </Text>
          </li>
          <li>
            <Text>
              <strong>Envio agendado:</strong> Selecione uma data e hora para iniciar o envio das mensagens
            </Text>
          </li>
          <li>
            <Text>
              <strong>Horários permitidos:</strong> As campanhas só podem ser enviadas nos horários configurados na conta
            </Text>
          </li>
          {throttlingEnabled ? (
            <li>
              <Text>
                <strong>Intervalos automáticos:</strong> As mensagens seguintes serão automaticamente espaçadas para evitar SPAM
              </Text>
            </li>
          ) : (
            <li>
              <Text type="danger">
                <strong>Sem intervalos:</strong> Todas as mensagens serão enviadas simultaneamente
              </Text>
            </li>
          )}
        </ul>
        
        {account?.campaignHours && (
          <div style={{ marginTop: 16, padding: 12, background: '#fafafa', borderRadius: 4 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              Resumo dos horários permitidos:
            </Text>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                const config = account.campaignHours[day];
                const dayNames = {
                  monday: 'Seg',
                  tuesday: 'Ter', 
                  wednesday: 'Qua',
                  thursday: 'Qui',
                  friday: 'Sex',
                  saturday: 'Sáb',
                  sunday: 'Dom'
                };
                
                if (!config) {
                  return (
                    <span key={day} style={{ marginRight: 8 }}>
                      <Tag color="red">{dayNames[day]}: Não configurado</Tag>
                    </span>
                  );
                }
                
                if (config.always_open) {
                  return (
                    <span key={day} style={{ marginRight: 8 }}>
                      <Tag color="green">{dayNames[day]}: 24h</Tag>
                    </span>
                  );
                }
                
                if (!config.intervals || config.intervals.length === 0) {
                  return (
                    <span key={day} style={{ marginRight: 8 }}>
                      <Tag color="red">{dayNames[day]}: Indisponível</Tag>
                    </span>
                  );
                }
                
                const intervals = config.intervals.map(interval => 
                  `${interval.start}-${interval.end}`
                ).join(', ');
                
                return (
                  <span key={day} style={{ marginRight: 8 }}>
                    <Tag color="blue">{dayNames[day]}: {intervals}</Tag>
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default SendStep;